import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../../components/Footer';
import { ArrowLeft, CheckCircle, MapPin, Phone } from 'lucide-react';

const locations = [
  'Scarsdale', 'Greenwich', 'Rye', 'Harrison', 'Mamaroneck', 'Bedford',
  'Chappaqua', 'Bronxville', 'Larchmont', 'Purchase'
];

const features = [
  {
    title: 'Complete Room Updates',
    description: 'Transform any room with comprehensive renovation services.'
  },
  {
    title: 'Wall Modifications',
    description: 'Expert wall removal or addition to optimize your space.'
  },
  {
    title: 'Flooring Installation',
    description: 'Wide selection of flooring options professionally installed.'
  },
  {
    title: 'Custom Trim Work',
    description: 'Beautiful trim and molding details to enhance your space.'
  },
  {
    title: 'Paint & Finishes',
    description: 'Professional painting and specialty finish applications.'
  },
  {
    title: 'Lighting Updates',
    description: 'Modern lighting solutions to brighten your space.'
  }
];

const portfolioImages = [
  {
    url: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&q=80&w=1200',
    location: 'Scarsdale Living Room'
  },
  {
    url: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&q=80&w=1200',
    location: 'Greenwich Family Room'
  },
  {
    url: 'https://images.unsplash.com/photo-1600573472591-711b45673d53?auto=format&fit=crop&q=80&w=1200',
    location: 'Rye Master Bedroom'
  }
];

export function InteriorRenovationsPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>Interior Renovation Services in Westchester County | Golden Touch</title>
        <meta name="description" content="Professional interior renovation services in Westchester County & Greenwich. From single room updates to whole-home renovations. Quality craftsmanship for every project." />
        <meta name="keywords" content="interior renovation Westchester, home remodeling Greenwich, room renovation Scarsdale, interior remodeling contractor" />
        <link rel="canonical" href="https://renovationwestchester.com/services/interior-renovations" />
      </Helmet>


      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Services</span>
          </Link>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
                Interior Renovation Services in Westchester
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Transform your living spaces with our comprehensive interior renovation services. 
                From single room updates to complete home renovations, we bring quality craftsmanship 
                to every project.
              </p>
              <div className="flex items-center gap-2 text-yellow-400 mb-8">
                <MapPin className="h-5 w-5" />
                <span className="text-sm tracking-wider">Westchester County & Greenwich Area</span>
              </div>
              <Link 
                to="/consultation"
                className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm tracking-wider">Schedule a Consultation</span>
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={portfolioImages[0].url}
                alt="Interior Renovation in Westchester"
                className="w-full h-full object-cover"
                width="1200"
                height="900"
              />
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Our Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-zinc-800/30 p-8">
                  <CheckCircle className="h-8 w-8 text-yellow-400 mb-4" />
                  <h3 className="text-xl text-zinc-100 mb-4">{feature.title}</h3>
                  <p className="text-zinc-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Areas Served</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {locations.map((location) => (
                <Link
                  key={location}
                  to={`/areas/${location.toLowerCase()}`}
                  className="block bg-zinc-800/30 p-4 text-center border border-transparent hover:border-yellow-400/40 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
                  <span className="text-zinc-300">{location}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-light text-zinc-100 mb-8">Ready to Transform Your Space?</h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Whether you're updating a single room or renovating your entire home, our team is ready 
              to help bring your vision to life with quality craftsmanship and attention to detail.
            </p>
            <Link 
              to="/consultation"
              className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm tracking-wider">Schedule Your Consultation</span>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}